import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useState, type FormEvent } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  categoriaExcluidaKeys,
  createCategoriaExcluida,
  deleteCategoriaExcluida,
  fetchCategoriasExcluidas,
} from '@/services/integracaoCategoriasExcluidasService';
import {
  createIntegracao,
  fetchIntegracoes,
  fetchSistemas,
  fetchUnidades,
  integracaoKeys,
  updateIntegracao,
} from '@/services/integracaoService';
import type { IIntegracao, TSistemaIntegracao } from '@/types/integracao';
import {
  LENUS_CREDENTIAL_TEMPLATE,
  SISTEMAS_INTEGRACAO,
} from '@/types/integracao';
import { useAnalytics, useErrors } from '@/observability';
import './integracoes.css';

export const Route = createFileRoute('/_authenticated/configuracao/integracoes/')({
  component: IntegracoesPage,
});

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') {
      return data.error;
    }
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

function parseCredencialJson(raw: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(raw);
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Credencial deve ser um objeto JSON.');
  }
  return parsed as Record<string, unknown>;
}

function templateForSistema(sistema: string): string {
  if (sistema === 'lenus') {
    return LENUS_CREDENTIAL_TEMPLATE;
  }
  return '{\n  \n}';
}

function CategoriasExcluidasModal({
  integracao,
  onClose,
}: {
  integracao: IIntegracao;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [idExterno, setIdExterno] = useState('');
  const [descricao, setDescricao] = useState('');
  const [motivo, setMotivo] = useState('');
  const [formError, setFormError] = useState('');

  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: categoriaExcluidaKeys.list(integracao.id),
    queryFn: () => fetchCategoriasExcluidas(integracao.id),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: categoriaExcluidaKeys.list(integracao.id) });

  const createMutation = useMutation({
    mutationFn: () =>
      createCategoriaExcluida(integracao.id, {
        id_externo: idExterno.trim(),
        descricao: descricao.trim() || null,
        motivo: motivo.trim() || null,
      }),
    onSuccess: () => {
      void invalidate();
      setIdExterno('');
      setDescricao('');
      setMotivo('');
      setFormError('');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao cadastrar categoria.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (categoriaId: string) => deleteCategoriaExcluida(integracao.id, categoriaId),
    onSuccess: () => void invalidate(),
    onError: (err) => setFormError(extractApiError(err, 'Falha ao remover categoria.')),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!idExterno.trim()) {
      setFormError('Informe o ID da categoria/agrupador no ERP.');
      return;
    }
    createMutation.mutate();
  }

  const pending = createMutation.isPending || deleteMutation.isPending;

  return (
    <Modal onClose={onClose} className="integracoes-page__modal integracoes-page__modal--wide">
      <Modal.Header title={`Categorias excluídas — ${integracao.sistema}`} />
      <Modal.Content>
        <p className="integracoes-page__help">
          Produtos cujo ID de categoria/agrupador no ERP estiver nesta lista são gravados no
          WMS na entrada, mas <strong>não</strong> disparam atualização de estoque na integração.
        </p>

        <form className="integracoes-page__form" onSubmit={onSubmit}>
          <div className="integracoes-page__field-group">
            <label className="integracoes-page__label" htmlFor="cat-id-externo">
              ID externo (agrupador)
            </label>
            <input
              id="cat-id-externo"
              type="text"
              value={idExterno}
              autoComplete="off"
              placeholder="ex.: 42"
              onChange={(e) => setIdExterno(e.target.value)}
            />
          </div>
          <div className="integracoes-page__field-group">
            <label className="integracoes-page__label" htmlFor="cat-descricao">
              Descrição (opcional)
            </label>
            <input
              id="cat-descricao"
              type="text"
              value={descricao}
              autoComplete="off"
              placeholder="Categoria X / Produção interna"
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          <div className="integracoes-page__field-group">
            <label className="integracoes-page__label" htmlFor="cat-motivo">
              Motivo (opcional)
            </label>
            <textarea
              id="cat-motivo"
              rows={2}
              value={motivo}
              spellCheck={false}
              placeholder="Por que não deve receber update no ERP"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
          {formError && (
            <p className="integracoes-page__form-alert integracoes-page__form-alert--error">
              {formError}
            </p>
          )}
          <button type="submit" className="integracoes-page__btn-submit" disabled={pending}>
            Adicionar
          </button>
        </form>

        <div className="integracoes-page__table-container integracoes-page__cat-table">
          <Table>
            <caption className="sr-only">Categorias excluídas do push de estoque</caption>
            <Table.Header>
              <Table.Row>
                <Table.Head scope="col">ID externo</Table.Head>
                <Table.Head scope="col">Descrição</Table.Head>
                <Table.Head scope="col">Motivo</Table.Head>
                <Table.Head scope="col">Ações</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {isLoading && (
                <Table.Row>
                  <Table.Cell colSpan={4} className="integracoes-page__empty">
                    Carregando…
                  </Table.Cell>
                </Table.Row>
              )}
              {isError && (
                <Table.Row>
                  <Table.Cell colSpan={4} className="integracoes-page__error">
                    {extractApiError(error, 'Falha ao carregar categorias.')}
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading &&
                !isError &&
                data.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell>{row.id_externo}</Table.Cell>
                    <Table.Cell>{row.descricao ?? '—'}</Table.Cell>
                    <Table.Cell>{row.motivo ?? '—'}</Table.Cell>
                    <Table.Cell className="integracoes-page__actions">
                      <button
                        type="button"
                        className="is-danger"
                        disabled={pending}
                        onClick={() => deleteMutation.mutate(row.id)}
                      >
                        Excluir
                      </button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              {!isLoading && !isError && data.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={4} className="integracoes-page__empty">
                    Nenhuma categoria excluída.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Content>
          </Table>
        </div>
      </Modal.Content>
      <Modal.Footer className="integracoes-page__modal-footer">
        <button type="button" className="integracoes-page__btn-secondary" onClick={onClose}>
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function IntegracoesPage() {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const errors = useErrors();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IIntegracao | null>(null);
  const [categoriasFor, setCategoriasFor] = useState<IIntegracao | null>(null);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [unidadeId, setUnidadeId] = useState('');
  const [sistema, setSistema] = useState<TSistemaIntegracao>('lenus');
  const [destino, setDestino] = useState('');
  const [credencialJson, setCredencialJson] = useState(LENUS_CREDENTIAL_TEMPLATE);
  const [ativo, setAtivo] = useState(true);

  const { data: unidades = [] } = useQuery({
    queryKey: integracaoKeys.unidades(),
    queryFn: fetchUnidades,
  });

  const { data: sistemasApi = [] } = useQuery({
    queryKey: integracaoKeys.sistemas(),
    queryFn: fetchSistemas,
  });

  const sistemas =
    sistemasApi.length > 0
      ? SISTEMAS_INTEGRACAO.filter((s) => sistemasApi.includes(s.value))
      : SISTEMAS_INTEGRACAO;

  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: integracaoKeys.list(),
    queryFn: fetchIntegracoes,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: integracaoKeys.all });

  const createMutation = useMutation({
    mutationFn: createIntegracao,
    onSuccess: () => {
      analytics.track('Integracao Saved', { action: 'create' });
      void invalidate();
      closeModal();
      setSuccessMessage('Integração criada.');
    },
    onError: (err) => {
      // Sem credencial nos logs/extra.
      errors.capture(err, { tags: { source: 'integracoes.create' } });
      setFormError(extractApiError(err, 'Falha ao criar integração.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updateIntegracao>[1] }) =>
      updateIntegracao(id, payload),
    onSuccess: () => {
      analytics.track('Integracao Saved', { action: 'update' });
      void invalidate();
      closeModal();
      setSuccessMessage('Integração atualizada.');
    },
    onError: (err) => {
      errors.capture(err, { tags: { source: 'integracoes.update' } });
      setFormError(extractApiError(err, 'Falha ao atualizar integração.'));
    },
  });

  const mutationPending = createMutation.isPending || updateMutation.isPending;

  const unidadeNome = (id: string) =>
    unidades.find((u) => u.id === id)?.nome ?? id;

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    setFormError('');
  }

  function openCreateModal() {
    setEditing(null);
    setUnidadeId(unidades[0]?.id ?? '');
    setSistema('lenus');
    setDestino('');
    setCredencialJson(LENUS_CREDENTIAL_TEMPLATE);
    setAtivo(true);
    setFormError('');
    setSuccessMessage('');
    setModalOpen(true);
  }

  function openEditModal(row: IIntegracao) {
    setEditing(row);
    setUnidadeId(row.unidade_id);
    setSistema(row.sistema as TSistemaIntegracao);
    setDestino(row.destino);
    setCredencialJson('');
    setAtivo(row.ativo);
    setFormError('');
    setSuccessMessage('');
    setModalOpen(true);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError('');

    if (!editing && !unidadeId) {
      setFormError('Selecione a unidade.');
      return;
    }
    if (!destino.trim()) {
      setFormError('Informe o destino (URL base ou connection string).');
      return;
    }

    let credencial: Record<string, unknown> | undefined;
    const raw = credencialJson.trim();
    if (raw) {
      try {
        credencial = parseCredencialJson(raw);
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'JSON de credencial inválido.');
        return;
      }
    } else if (!editing) {
      setFormError('Informe a credencial em JSON.');
      return;
    }

    if (editing) {
      updateMutation.mutate({
        id: editing.id,
        payload: {
          destino: destino.trim(),
          ativo,
          ...(credencial ? { credencial } : {}),
        },
      });
      return;
    }

    createMutation.mutate({
      unidade_id: unidadeId,
      sistema,
      destino: destino.trim(),
      credencial: credencial!,
    });
  }

  return (
    <div className="integracoes-page">
      <div className="integracoes-page__header">
        <p className="integracoes-page__subtitle">
          Vincule unidades do tenant a sistemas externos (API ou banco).
        </p>
        <button
          type="button"
          className="integracoes-page__btn-primary"
          disabled={mutationPending || unidades.length === 0}
          onClick={openCreateModal}
        >
          Nova integração
        </button>
      </div>

      {successMessage && (
        <p className="integracoes-page__success" role="status" aria-live="polite">
          {successMessage}
        </p>
      )}
      {isError && (
        <p className="integracoes-page__error">
          {extractApiError(error, 'Falha ao carregar integrações.')}
        </p>
      )}
      {!modalOpen && updateMutation.isError && (
        <p className="integracoes-page__error">
          {extractApiError(updateMutation.error, 'Falha ao atualizar integração.')}
        </p>
      )}

      <div className="integracoes-page__table-container">
        <Table>
          <caption className="sr-only">Integrações configuradas</caption>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Unidade</Table.Head>
              <Table.Head scope="col">Sistema</Table.Head>
              <Table.Head scope="col">Destino</Table.Head>
              <Table.Head scope="col">Credencial</Table.Head>
              <Table.Head scope="col">Status</Table.Head>
              <Table.Head scope="col">Ações</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={6} className="integracoes-page__empty">
                  Carregando…
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading &&
              data.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>{unidadeNome(row.unidade_id)}</Table.Cell>
                  <Table.Cell>{row.sistema}</Table.Cell>
                  <Table.Cell className="integracoes-page__destino">{row.destino}</Table.Cell>
                  <Table.Cell>
                    {row.credencial_configurada ? 'Configurada' : '—'}
                  </Table.Cell>
                  <Table.Cell>{row.ativo ? 'Ativa' : 'Inativa'}</Table.Cell>
                  <Table.Cell className="integracoes-page__actions">
                    <button
                      type="button"
                      disabled={mutationPending}
                      onClick={() => openEditModal(row)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      disabled={mutationPending}
                      onClick={() => setCategoriasFor(row)}
                    >
                      Categorias
                    </button>
                    {row.ativo && (
                      <button
                        type="button"
                        className="is-danger"
                        disabled={mutationPending}
                        onClick={() => {
                          setSuccessMessage('');
                          updateMutation.mutate({
                            id: row.id,
                            payload: { ativo: false },
                          });
                        }}
                      >
                        Desativar
                      </button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            {!isLoading && !isError && data.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={6} className="integracoes-page__empty">
                  Nenhuma integração configurada.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Content>
        </Table>
      </div>

      {modalOpen && (
        <Modal onClose={closeModal} className="integracoes-page__modal">
          <Modal.Header title={editing ? 'Editar integração' : 'Nova integração'} />
          <Modal.Content>
            <form id="integracao-form" className="integracoes-page__form" onSubmit={onSubmit}>
              <div className="integracoes-page__field-group">
                <label className="integracoes-page__label" htmlFor="unidade">
                  Unidade
                </label>
                <select
                  id="unidade"
                  value={unidadeId}
                  disabled={Boolean(editing)}
                  onChange={(e) => setUnidadeId(e.target.value)}
                >
                  {unidades.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="integracoes-page__field-group">
                <label className="integracoes-page__label" htmlFor="sistema">
                  Sistema
                </label>
                <select
                  id="sistema"
                  value={sistema}
                  disabled={Boolean(editing)}
                  onChange={(e) => {
                    const next = e.target.value as TSistemaIntegracao;
                    setSistema(next);
                    if (!editing) {
                      setCredencialJson(templateForSistema(next));
                    }
                  }}
                >
                  {sistemas.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="integracoes-page__field-group">
                <label className="integracoes-page__label" htmlFor="destino">
                  Destino (URL base / connection string)
                </label>
                <input
                  id="destino"
                  type="text"
                  value={destino}
                  autoComplete="off"
                  placeholder="https://api.exemplo.com"
                  onChange={(e) => setDestino(e.target.value)}
                />
              </div>

              <div className="integracoes-page__field-group">
                <label className="integracoes-page__label" htmlFor="credencial">
                  Credencial (JSON)
                  {editing ? ' — vazio mantém a atual' : ''}
                </label>
                <textarea
                  id="credencial"
                  rows={8}
                  value={credencialJson}
                  spellCheck={false}
                  placeholder={editing ? 'Deixe vazio para manter a credencial atual' : ''}
                  onChange={(e) => setCredencialJson(e.target.value)}
                />
              </div>

              {editing && (
                <div className="integracoes-page__field-group integracoes-page__field-group--row">
                  <label className="integracoes-page__label" htmlFor="ativo">
                    <input
                      id="ativo"
                      type="checkbox"
                      checked={ativo}
                      onChange={(e) => setAtivo(e.target.checked)}
                    />
                    {' '}Ativa
                  </label>
                </div>
              )}

              {formError && (
                <p className="integracoes-page__form-alert integracoes-page__form-alert--error">
                  {formError}
                </p>
              )}
            </form>
          </Modal.Content>
          <Modal.Footer className="integracoes-page__modal-footer">
            <button
              type="button"
              className="integracoes-page__btn-secondary"
              disabled={mutationPending}
              onClick={closeModal}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="integracao-form"
              className="integracoes-page__btn-submit"
              disabled={mutationPending}
            >
              {editing ? 'Salvar' : 'Criar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {categoriasFor && (
        <CategoriasExcluidasModal
          integracao={categoriasFor}
          onClose={() => setCategoriasFor(null)}
        />
      )}
    </div>
  );
}
